import { Button, Center, Group, Stack, Title, useMatches } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import logoCarburate from "@/assets/illustrations/logo-carburate.svg";
import walkthrough2 from "@/assets/walkthrough/2.png";
import walkthroughInspect from "@/assets/walkthrough/3.png";
import walkthroughMarket from "@/assets/walkthrough/4.png";
import walkthroughAuctions from "@/assets/walkthrough/5.png";
import { CarburateLogo, Carousel, TextSerif } from "@/components/elements";

type WalkthroughStep = {
  key: string;
  kind: "mark" | "image";
  src: string;
  title: string;
  description: string;
  cta: string;
};

export type WalkthroughProps = {
  onDone?: () => void;
  onStepChange?: (step: number) => void;
  compact?: boolean;
};

export const Walkthrough = ({ onDone, onStepChange, compact = false }: WalkthroughProps) => {
  const { t } = useTranslation();

  const isFullscreenModal = useMatches({ base: true, md: false });
  const { height: viewportHeight, width: viewportWidth } = useViewportSize();
  const isLandscape = viewportWidth > viewportHeight;
  const isConstrainedLandscape = isLandscape && viewportHeight <= 720;
  const isCompactLandscape = !compact && isConstrainedLandscape;
  const isMobile = isFullscreenModal || isCompactLandscape;

  const steps = useMemo<WalkthroughStep[]>(
    () => [
      {
        key: "welcome",
        kind: "image",
        src: logoCarburate,
        title: t("components.walkthrough.steps.welcome.title"),
        description: t("components.walkthrough.steps.welcome.description"),
        cta: "",
      },
      {
        key: "garage",
        kind: "mark",
        src: walkthrough2,
        title: t("components.walkthrough.steps.garage.title"),
        description: t("components.walkthrough.steps.garage.description"),
        cta: "",
      },
      {
        key: "inspect",
        kind: "mark",
        src: walkthroughInspect,
        title: t("components.walkthrough.steps.inspect.title"),
        description: t("components.walkthrough.steps.inspect.description"),
        cta: "",
      },
      {
        key: "market",
        kind: "mark",
        src: walkthroughMarket,
        title: t("components.walkthrough.steps.market.title"),
        description: t("components.walkthrough.steps.market.description"),
        cta: "",
      },
      {
        key: "auctions",
        kind: "mark",
        src: walkthroughAuctions,
        title: t("components.walkthrough.steps.auctions.title"),
        description: t("components.walkthrough.steps.auctions.description"),
        cta: "",
      },
    ],
    [t]
  );

  const [active, setActive] = useState(0);
  const [embla, setEmbla] = useState<any>(null);

  const handleStepChange = (next: number) => {
    setActive(next);
    onStepChange?.(next);
  };

  const onCtaClick = () => {
    const isLast = active >= steps.length - 1;
    if (isLast) onDone?.();
    else embla?.scrollNext?.();
  };

  const modalChromeHeight = isFullscreenModal ? 60 : 112;
  const availableHeight = Math.max(0, viewportHeight - modalChromeHeight);
  const TOP_LOGO_H = isCompactLandscape ? 24 : isMobile ? 40 : 64;
  const carouselHeight = isCompactLandscape
    ? Math.min(150, availableHeight * 0.48)
    : compact
      ? isMobile
        ? 110
        : 180
      : isMobile
        ? 150
        : 316;
  const imageHeight = isCompactLandscape
    ? carouselHeight
    : compact
      ? isMobile
        ? 90
        : 150
      : isMobile
        ? 110
        : 160;
  const markContainerHeight = isCompactLandscape
    ? carouselHeight
    : compact
      ? isMobile
        ? 100
        : 160
      : isMobile
        ? 120
        : 270;
  const markImageMaxHeight = isCompactLandscape
    ? Math.max(0, carouselHeight - 4)
    : compact
      ? isMobile
        ? 80
        : 140
      : isMobile
        ? 100
        : 250;
  const activeStep = steps[active];
  const isLast = active >= steps.length - 1;
  const ctaLabel = isLast
    ? t("components.walkthrough.cta.startYourAdventure")
    : active === 0
      ? t("components.walkthrough.cta.startNow")
      : t("components.walkthrough.cta.continue");

  return (
    <Stack
      gap="md"
      px={0}
      pt={
        isCompactLandscape ? 2 : compact ? (isMobile ? 4 : 8) : isMobile ? 4 : { base: 12, sm: 16 }
      }
      pb={
        isCompactLandscape ? 2 : compact ? (isMobile ? 8 : 12) : isMobile ? 8 : { base: 16, sm: 30 }
      }
      style={{
        boxSizing: "border-box",
        height: isCompactLandscape ? availableHeight : undefined,
        maxHeight: isCompactLandscape ? availableHeight : undefined,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
      }}
    >
      {!compact && !isCompactLandscape && (
        <Center
          h={TOP_LOGO_H}
          pos="relative"
          bottom={isCompactLandscape ? 0 : isMobile ? 8 : 20}
          style={{ width: "100%", flexShrink: isCompactLandscape ? 1 : 0, minHeight: 0 }}
        >
          <CarburateLogo fill="#FFFFFF" w={isMobile ? 150 : 210} />
        </Center>
      )}

      <Carousel
        initialSlide={0}
        height={carouselHeight}
        slideSize="100%"
        slideGap={0}
        withIndicators={false}
        withControls={false}
        emblaOptions={{ loop: false }}
        pb={0}
        onSlideChange={handleStepChange}
        getEmblaApi={setEmbla}
        styles={{
          root: {
            width: "100%",
            minWidth: 0,
            minHeight: 0,
            flexShrink: isCompactLandscape ? 1 : 0,
          },
          viewport: { overflow: "hidden" },
          container: { height: "100%" },
        }}
      >
        {steps.map((step) => (
          <Carousel.Slide
            key={step.key}
            style={{ paddingInline: isMobile ? 4 : 6, height: "100%" }}
          >
            <Stack
              gap={isMobile ? 4 : "md"}
              align="center"
              justify={step.kind === "image" ? "flex-start" : "center"}
              style={{ height: "100%", minHeight: 0, width: "100%" }}
            >
              {step.kind === "image" && (
                <Center
                  pt={compact ? (isMobile ? 0 : 12) : isMobile ? 0 : 4}
                  style={{ maxHeight: "100%", minHeight: 0 }}
                >
                  <img
                    src={step.src}
                    alt="Carburate"
                    style={{
                      width: isMobile ? (compact ? 70 : 85) : 110,
                      height: imageHeight,
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Center>
              )}

              {step.kind === "mark" && (
                <Center
                  style={{
                    width: "100%",
                    height: markContainerHeight,
                    maxHeight: "100%",
                    minHeight: 0,
                    flexShrink: 1,
                  }}
                >
                  <img
                    src={step.src}
                    alt={step.title}
                    style={{
                      width: isMobile ? "100%" : "min(560px, 100%)",
                      maxWidth: "100%",
                      maxHeight: markImageMaxHeight,
                      objectFit: "contain",
                      height: "auto",
                    }}
                  />
                </Center>
              )}
            </Stack>
          </Carousel.Slide>
        ))}
      </Carousel>

      <Group
        justify="center"
        gap={isCompactLandscape ? 4 : 6}
        mt={isCompactLandscape ? 4 : isMobile ? 6 : 12}
        style={{
          flexShrink: 0,
          visibility: active > 0 ? "visible" : "hidden",
          pointerEvents: active > 0 ? "auto" : "none",
        }}
      >
        {steps.map((step, index) => (
          <Center
            key={step.key}
            component="button"
            type="button"
            aria-label={`Go to step ${index + 1}`}
            aria-current={index === active ? "step" : undefined}
            onClick={() => embla?.scrollTo?.(index)}
            style={{
              width:
                index === active
                  ? isCompactLandscape
                    ? 12
                    : isMobile
                      ? 20
                      : 28
                  : isCompactLandscape
                    ? 4
                    : 8,
              height: isCompactLandscape ? 4 : 8,
              border: "none",
              borderRadius: 999,
              padding: 0,
              cursor: "pointer",
              backgroundColor:
                index === active
                  ? "var(--mantine-color-primary-6)"
                  : "var(--mantine-color-secondary-1)",
              transition: "width 250ms ease, background-color 250ms ease",
            }}
          />
        ))}
      </Group>

      <Center
        my={isCompactLandscape ? 8 : compact ? (isMobile ? 8 : 16) : isMobile ? 6 : 12}
        w="100%"
        style={{
          minHeight: 0,
          minWidth: 0,
          flex: isCompactLandscape ? "0 1 auto" : undefined,
          flexShrink: isCompactLandscape ? 1 : 0,
        }}
      >
        <Stack
          gap={isCompactLandscape ? 2 : isMobile ? 4 : compact ? 10 : 8}
          align="center"
          maw={440}
          w="100%"
          px={isMobile ? 8 : "sm"}
          style={{ minHeight: 0 }}
        >
          <Title
            order={2}
            c="#FFFFFF"
            ta="center"
            fw={600}
            w="100%"
            style={{
              letterSpacing: -0.2,
              fontSize: isCompactLandscape ? 15 : isMobile ? (compact ? 18 : 20) : undefined,
              lineHeight: isCompactLandscape ? 1.1 : isMobile ? 1.2 : undefined,
              overflowWrap: "anywhere",
            }}
          >
            {activeStep.title}
          </Title>
          <TextSerif
            c="#FFFFFF"
            ta="center"
            fz={isMobile ? "xs" : "sm"}
            w="100%"
            style={{
              whiteSpace: "pre-line",
              fontSize: isCompactLandscape ? 10 : undefined,
              lineHeight: isCompactLandscape ? 1.15 : isMobile ? 1.3 : 1.4,
              textWrap: "pretty",
              overflowWrap: "anywhere",
            }}
          >
            {activeStep.description}
          </TextSerif>
        </Stack>
      </Center>

      <Group
        justify="center"
        gap="sm"
        wrap="nowrap"
        mt={isCompactLandscape ? 8 : compact ? (isMobile ? 8 : 20) : isMobile ? 6 : 12}
        px={isMobile ? 8 : 0}
        style={{ width: "100%", flexShrink: 0 }}
      >
        <Button
          onClick={onCtaClick}
          w={isMobile ? "100%" : 250}
          maw={250}
          radius="md"
          styles={{
            root: {
              backgroundColor: "var(--mantine-color-primary-6)",
              flexShrink: 0,
            },
            label: { letterSpacing: 0.8, fontWeight: 600 },
          }}
        >
          {ctaLabel}
        </Button>
      </Group>
    </Stack>
  );
};
